import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add as AddIcon,
  AddBox as AddBoxIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { debtAPI, expenseAPI, incomeAPI } from "../../services/api/api";
import { Debt, Expense, Income } from "../../lib/types";
import { settingsService } from "../../services/settings/settingsService";

type VendorFlowRow = {
  vendor: string;
  amount: number;
};

type ChartViewType = "flow" | "bars" | "donut";
type DatasetType = "income" | "expenses" | "debts";

type ChartWidget = {
  id: string;
  dataset: DatasetType;
  view: ChartViewType;
  zoom: number;
  gradientStart: string;
  gradientEnd: string;
};

type ChartColumn = {
  id: string;
  widgets: ChartWidget[];
};

type ChartRow = {
  id: string;
  columns: ChartColumn[];
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);

const normalizeVendor = (value?: string) => {
  const next = String(value || "").trim();
  return next.length > 0 ? next : "Unassigned";
};

const buildVendorRows = (
  values: Map<string, number>,
  options?: { topN?: number }
): VendorFlowRow[] => {
  const topN = options?.topN ?? 14;
  const sorted = Array.from(values.entries())
    .map(([vendor, amount]) => ({ vendor, amount }))
    .filter((entry) => entry.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  if (sorted.length <= topN) return sorted;

  const head = sorted.slice(0, topN);
  const remainingTotal = sorted
    .slice(topN)
    .reduce((sum, entry) => sum + entry.amount, 0);
  if (remainingTotal > 0) {
    head.push({ vendor: "Other", amount: remainingTotal });
  }
  return head;
};

const clampZoom = (value: number) => Math.max(0.6, Math.min(2.4, value));

const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createWidget = (): ChartWidget => ({
  id: createId(),
  dataset: "income",
  view: "flow",
  zoom: 1.25,
  gradientStart: "#10B981",
  gradientEnd: "#3B82F6",
});

const createColumn = (): ChartColumn => ({ id: createId(), widgets: [] });

const isValidView = (value: unknown): value is ChartViewType =>
  value === "flow" || value === "bars" || value === "donut";
const isValidDataset = (value: unknown): value is DatasetType =>
  value === "income" || value === "expenses" || value === "debts";

const normalizeChartsLayout = (input: unknown): ChartRow[] => {
  if (!Array.isArray(input)) return [];
  const rows: ChartRow[] = [];
  input.forEach((rowRaw) => {
    const rowObj = rowRaw as any;
    const columnsRaw = Array.isArray(rowObj?.columns) ? rowObj.columns : [];
    const columns: ChartColumn[] = columnsRaw.map((colRaw: any) => {
      const widgetsRaw = Array.isArray(colRaw?.widgets) ? colRaw.widgets : [];
      const widgets: ChartWidget[] = widgetsRaw
        .map((wRaw: any) => {
          const dataset: DatasetType = isValidDataset(wRaw?.dataset)
            ? wRaw.dataset
            : "income";
          const view = isValidView(wRaw?.view) ? wRaw.view : "flow";
          const zoom =
            typeof wRaw?.zoom === "number" ? clampZoom(wRaw.zoom) : 1.25;
          const gradientStart =
            typeof wRaw?.gradientStart === "string"
              ? wRaw.gradientStart
              : datasetMeta[dataset].color;
          const gradientEnd =
            typeof wRaw?.gradientEnd === "string"
              ? wRaw.gradientEnd
              : "#3B82F6";
          return {
            id: typeof wRaw?.id === "string" ? wRaw.id : createId(),
            dataset,
            view,
            zoom,
            gradientStart,
            gradientEnd,
          };
        })
        .filter(Boolean);
      return {
        id: typeof colRaw?.id === "string" ? colRaw.id : createId(),
        widgets,
      };
    });
    rows.push({
      id: typeof rowObj?.id === "string" ? rowObj.id : createId(),
      columns: columns.length > 0 ? columns : [createColumn()],
    });
  });
  return rows;
};

const getDonutSegments = (rows: VendorFlowRow[]) => {
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  if (total <= 0) return [];
  let running = 0;
  return rows.map((row) => {
    const start = (running / total) * 360;
    running += row.amount;
    const end = (running / total) * 360;
    return { ...row, start, end, percent: (row.amount / total) * 100 };
  });
};

const hexToRgb = (hex: string) => {
  const clean = hex.replace("#", "");
  const normalized =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value) || normalized.length !== 6) {
    return { r: 16, g: 185, b: 129 };
  }
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`;

const buildGradientPalette = (
  startHex: string,
  endHex: string,
  count: number
) => {
  if (count <= 0) return [];
  if (count === 1) return [startHex];
  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);
  return Array.from({ length: count }, (_, idx) => {
    const t = idx / (count - 1);
    return rgbToHex(
      start.r + (end.r - start.r) * t,
      start.g + (end.g - start.g) * t,
      start.b + (end.b - start.b) * t
    );
  });
};

const polarToCartesian = (
  cx: number,
  cy: number,
  radius: number,
  angle: number
) => {
  const radians = ((angle - 90) * Math.PI) / 180.0;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

const describeArc = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
) => {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const VendorBarsChart: React.FC<{
  rows: VendorFlowRow[];
  palette: string[];
  zoom: number;
}> = ({ rows, palette, zoom }) => {
  const maxValue = Math.max(1, ...rows.map((row) => row.amount));
  const labelSize = Math.max(10, 12 * zoom);
  return (
    <Stack spacing={0.9} sx={{ pt: 0.5 }}>
      {rows.map((row, index) => (
        <Box key={row.vendor}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.25 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: labelSize }}
            >
              {row.vendor}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, fontSize: labelSize }}
            >
              {formatCurrency(row.amount)}
            </Typography>
          </Stack>
          <Box
            sx={{
              height: Math.max(6, 8 * zoom),
              borderRadius: 999,
              bgcolor: "action.hover",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${Math.max(3, (row.amount / maxValue) * 100)}%`,
                height: "100%",
                bgcolor: palette[index % palette.length] || "#10B981",
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const VendorDonutChart: React.FC<{
  rows: VendorFlowRow[];
  zoom: number;
  palette: string[];
}> = ({ rows, zoom, palette }) => {
  const segments = getDonutSegments(rows);
  const size = Math.round(220 * zoom);
  const radius = Math.round(76 * zoom);
  const center = size / 2;
  const ring = Math.max(10, Math.round(28 * zoom));
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      alignItems="center"
    >
      <svg
        width={size}
        height={size}
        role="img"
        aria-label="Vendor donut chart"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={ring}
        />
        {segments.map((segment, index) => (
          <path
            key={`${segment.vendor}-${index}`}
            d={describeArc(center, center, radius, segment.start, segment.end)}
            fill="none"
            stroke={palette[index % palette.length] || "#10B981"}
            strokeWidth={ring}
            strokeLinecap="butt"
          />
        ))}
        <circle
          cx={center}
          cy={center}
          r={Math.max(20, Math.round(50 * zoom))}
          fill="currentColor"
          opacity={0.06}
        />
      </svg>
      <Stack spacing={0.5} sx={{ minWidth: 200 }}>
        {segments.slice(0, 8).map((segment, index) => (
          <Stack
            key={segment.vendor}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.75}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: palette[index % palette.length] || "#10B981",
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: Math.max(10, 12 * zoom) }}
              >
                {segment.vendor}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, fontSize: Math.max(10, 12 * zoom) }}
            >
              {segment.percent.toFixed(1)}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const VendorFlowChart: React.FC<{
  title: string;
  rootLabel: string;
  rows: VendorFlowRow[];
  view: ChartViewType;
  zoom: number;
  gradientStart: string;
  gradientEnd: string;
}> = ({ title, rootLabel, rows, view, zoom, gradientStart, gradientEnd }) => {
  const theme = useTheme();
  const maxValue = Math.max(1, ...rows.map((row) => row.amount));
  const width = Math.round(620 * zoom);
  const height = Math.round(Math.max(180, 68 + rows.length * 24) * zoom);
  const rootX = Math.round(136 * zoom);
  const rootY = height / 2;
  const vendorX = Math.round(460 * zoom);
  const startY = Math.round(40 * zoom);
  const rowSpacing = Math.round(24 * zoom);
  const isDarkMode = theme.palette.mode === "dark";
  const palette = buildGradientPalette(
    gradientStart,
    gradientEnd,
    Math.max(rows.length, 2)
  );
  const rootColor = palette[0] || gradientStart;
  const rootFill = alpha(rootColor, isDarkMode ? 0.22 : 0.14);
  const rootStroke = alpha(rootColor, isDarkMode ? 0.6 : 0.45);

  return (
    <Card
      sx={{
        borderRadius: 2,
        backgroundColor: isDarkMode ? "#111827" : "#ffffff",
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No vendor data available.
          </Typography>
        ) : view === "bars" ? (
          <VendorBarsChart rows={rows} palette={palette} zoom={zoom} />
        ) : view === "donut" ? (
          <VendorDonutChart rows={rows} zoom={zoom} palette={palette} />
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <svg
              width={width}
              height={height}
              style={{ maxWidth: "100%", height: "auto", display: "block" }}
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label={title}
            >
              <rect
                x={rootX - 54 * zoom}
                y={rootY - 16 * zoom}
                width={108 * zoom}
                height={32 * zoom}
                rx={8 * zoom}
                fill={rootFill}
                stroke={rootStroke}
              />
              <text
                x={rootX}
                y={rootY + 4 * zoom}
                textAnchor="middle"
                fill={isDarkMode ? "#E5E7EB" : "#1E293B"}
                fontSize={10 * zoom}
                fontWeight="700"
              >
                {rootLabel}
              </text>

              {rows.map((row, index) => {
                const lineColor =
                  palette[index % palette.length] || gradientStart;
                const nodeFill = alpha(lineColor, isDarkMode ? 0.22 : 0.14);
                const nodeStroke = alpha(lineColor, isDarkMode ? 0.6 : 0.45);
                const vendorY = startY + index * rowSpacing;
                const thickness = (1 + (row.amount / maxValue) * 5) * zoom;
                const curve = `M ${rootX + 54 * zoom} ${rootY} C ${
                  rootX + 126 * zoom
                } ${rootY}, ${vendorX - 126 * zoom} ${vendorY}, ${
                  vendorX - 54 * zoom
                } ${vendorY}`;
                return (
                  <g key={`${row.vendor}-${index}`}>
                    <path
                      d={curve}
                      stroke={lineColor}
                      strokeWidth={thickness}
                      strokeLinecap="round"
                      fill="none"
                      opacity={0.85}
                    />
                    <rect
                      x={vendorX - 54 * zoom}
                      y={vendorY - 10 * zoom}
                      width={172 * zoom}
                      height={20 * zoom}
                      rx={6 * zoom}
                      fill={nodeFill}
                      stroke={nodeStroke}
                    />
                    <text
                      x={vendorX - 45 * zoom}
                      y={vendorY + 2 * zoom}
                      textAnchor="start"
                      fill={isDarkMode ? "#E5E7EB" : "#111827"}
                      fontSize={9 * zoom}
                      fontWeight="600"
                    >
                      {row.vendor}
                    </text>
                    <text
                      x={vendorX + 108 * zoom}
                      y={vendorY + 2 * zoom}
                      textAnchor="end"
                      fill={isDarkMode ? "#A7F3D0" : "#065F46"}
                      fontSize={9 * zoom}
                      fontWeight="700"
                    >
                      {formatCurrency(row.amount)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const datasetMeta: Record<
  DatasetType,
  { title: string; rootLabel: string; color: string }
> = {
  income: {
    title: "Income by Vendor",
    rootLabel: "Income Total",
    color: "#10B981",
  },
  expenses: {
    title: "Expenses by Vendor",
    rootLabel: "Expenses Total",
    color: "#EF4444",
  },
  debts: {
    title: "Debts by Creditor",
    rootLabel: "Debt Total",
    color: "#F59E0B",
  },
};

const Charts: React.FC = () => {
  const [income, setIncome] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ChartRow[]>([
    {
      id: createId(),
      columns: [
        {
          id: createId(),
          widgets: [
            {
              ...createWidget(),
              dataset: "income",
            },
          ],
        },
      ],
    },
  ]);
  const [addChartOpen, setAddChartOpen] = useState(false);
  const [addChartTarget, setAddChartTarget] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);
  const [newChart, setNewChart] = useState<Omit<ChartWidget, "id">>({
    dataset: "income",
    view: "flow",
    zoom: 1.25,
    gradientStart: "#10B981",
    gradientEnd: "#3B82F6",
  });
  const hasHydratedLayoutRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  const [dragging, setDragging] = useState<{
    widgetId: string;
    fromRowId: string;
    fromColId: string;
  } | null>(null);
  const [dropHover, setDropHover] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);

  const folderTabsSx = {
    "&.MuiToggleButtonGroup-root": {
      borderRadius: 0,
      borderBottom: "1px solid",
      borderColor: "divider",
      gap: 0.5,
    },
    "& .MuiToggleButton-root": {
      border: "1px solid",
      borderColor: "divider",
      borderBottom: "none",
      borderRadius: "8px 8px 0 0",
      px: 1.5,
      py: 0.45,
      textTransform: "none",
      fontWeight: 600,
      minWidth: 64,
      color: "text.secondary",
      bgcolor: "action.hover",
    },
    "& .MuiToggleButton-root.Mui-selected": {
      color: "text.primary",
      bgcolor: "background.paper",
      borderColor: "divider",
    },
    "& .MuiToggleButton-root:not(:first-of-type)": {
      marginLeft: 0,
      borderLeft: "1px solid",
      borderColor: "divider",
    },
  } as const;

  const tabBodySx = {
    border: "1px solid",
    borderColor: "divider",
    borderTop: "none",
    borderRadius: "0 10px 10px 10px",
    bgcolor: "background.paper",
    p: 1,
    "& .MuiCard-root": {
      borderRadius: 0,
      boxShadow: "none",
      bgcolor: "transparent",
    },
    "& .MuiCardContent-root": {
      p: "8px !important",
    },
  } as const;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [incomeData, expenseData, debtData, userSettings] =
          await Promise.all([
            incomeAPI.getAll(),
            expenseAPI.getAll(),
            debtAPI.getAll(),
            settingsService.getUserSettings(),
          ]);
        setIncome(incomeData);
        setExpenses(expenseData);
        setDebts(debtData);
        const normalized = normalizeChartsLayout(userSettings.charts_layout);
        if (normalized.length > 0) {
          setRows(normalized);
        }
      } catch (err) {
        console.error("[Charts] Failed to load chart data:", err);
        setError("Failed to load chart data.");
      } finally {
        hasHydratedLayoutRef.current = true;
        setLoading(false);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (!hasHydratedLayoutRef.current) return;
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      void settingsService.saveUserSettings({
        charts_layout: rows as unknown[],
      });
    }, 500);
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [rows]);

  const incomeRows = useMemo(() => {
    const totals = new Map<string, number>();
    income.forEach((item) => {
      const vendor = normalizeVendor(item.client);
      const invoiceTotal = Number(item.invoiceTotal || 0);
      const fee = Number(item.paymentProcessorFee || 0);
      const loan = Number(item.loanRepayment || 0);
      const amount = Math.max(0, invoiceTotal - fee - loan);
      if (amount <= 0) return;
      totals.set(vendor, (totals.get(vendor) || 0) + amount);
    });
    return buildVendorRows(totals);
  }, [income]);

  const expenseRows = useMemo(() => {
    const totals = new Map<string, number>();
    expenses.forEach((item) => {
      if (item.isSkipped) return;
      const vendor = normalizeVendor(item.vendor);
      const amount = Math.abs(Number(item.amount || 0));
      if (amount <= 0) return;
      totals.set(vendor, (totals.get(vendor) || 0) + amount);
    });
    return buildVendorRows(totals);
  }, [expenses]);

  const debtRows = useMemo(() => {
    const totals = new Map<string, number>();
    debts.forEach((item) => {
      const vendor = normalizeVendor(item.creditor || item.account);
      const amount = Math.abs(Number(item.balance || 0));
      if (amount <= 0) return;
      totals.set(vendor, (totals.get(vendor) || 0) + amount);
    });
    return buildVendorRows(totals);
  }, [debts]);

  const rowsByDataset: Record<DatasetType, VendorFlowRow[]> = {
    income: incomeRows,
    expenses: expenseRows,
    debts: debtRows,
  };

  const getWidgetById = (widgetId: string): ChartWidget | null => {
    for (const row of rows) {
      for (const col of row.columns) {
        const found = col.widgets.find((w) => w.id === widgetId);
        if (found) return found;
      }
    }
    return null;
  };

  const renderWidgetCard = (
    widget: ChartWidget,
    context: { rowId: string; colId: string; ghost?: boolean }
  ) => {
    const { rowId, colId, ghost } = context;
    const meta = datasetMeta[widget.dataset];
    const selectedRows = rowsByDataset[widget.dataset];

    return (
      <Box
        key={ghost ? `ghost-${widget.id}` : widget.id}
        draggable={!ghost}
        onDragStart={
          ghost
            ? undefined
            : () =>
                setDragging({
                  widgetId: widget.id,
                  fromRowId: rowId,
                  fromColId: colId,
                })
        }
        onDragEnd={
          ghost
            ? undefined
            : () => {
                setDragging(null);
                setDropHover(null);
              }
        }
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
          p: 0.75,
          opacity: ghost ? 0.15 : 1,
          pointerEvents: ghost ? "none" : "auto",
        }}
      >
        <Stack spacing={0}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DragIndicatorIcon fontSize="small" color="disabled" />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id={`dataset-${widget.id}`}>Dataset</InputLabel>
                <Select
                  labelId={`dataset-${widget.id}`}
                  value={widget.dataset}
                  label="Dataset"
                  disabled={ghost}
                  onChange={(event) =>
                    updateWidget(rowId, colId, widget.id, {
                      dataset: event.target.value as DatasetType,
                    })
                  }
                >
                  <MenuItem value="income">Income</MenuItem>
                  <MenuItem value="expenses">Expenses</MenuItem>
                  <MenuItem value="debts">Debts</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip title="Gradient start">
                <Box
                  component="input"
                  type="color"
                  value={widget.gradientStart}
                  disabled={ghost}
                  onChange={(event) =>
                    updateWidget(rowId, colId, widget.id, {
                      gradientStart: event.target.value,
                    })
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    border: "none",
                    background: "transparent",
                    cursor: ghost ? "default" : "pointer",
                  }}
                />
              </Tooltip>
              <Tooltip title="Gradient end">
                <Box
                  component="input"
                  type="color"
                  value={widget.gradientEnd}
                  disabled={ghost}
                  onChange={(event) =>
                    updateWidget(rowId, colId, widget.id, {
                      gradientEnd: event.target.value,
                    })
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    border: "none",
                    background: "transparent",
                    cursor: ghost ? "default" : "pointer",
                  }}
                />
              </Tooltip>
              <Tooltip title="Zoom out">
                <span>
                  <IconButton
                    size="small"
                    disabled={ghost}
                    onClick={() =>
                      updateWidget(rowId, colId, widget.id, {
                        zoom: clampZoom(widget.zoom - 0.1),
                      })
                    }
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{ minWidth: 38, textAlign: "center" }}
              >
                {Math.round(widget.zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom in">
                <span>
                  <IconButton
                    size="small"
                    disabled={ghost}
                    onClick={() =>
                      updateWidget(rowId, colId, widget.id, {
                        zoom: clampZoom(widget.zoom + 0.1),
                      })
                    }
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reset zoom">
                <span>
                  <IconButton
                    size="small"
                    disabled={ghost}
                    onClick={() =>
                      updateWidget(rowId, colId, widget.id, {
                        zoom: 1.25,
                      })
                    }
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {!ghost && (
                <Tooltip title="Remove chart">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeWidget(rowId, colId, widget.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={widget.view}
            disabled={ghost}
            onChange={(event, value) =>
              value &&
              updateWidget(rowId, colId, widget.id, {
                view: value,
              })
            }
            sx={folderTabsSx}
          >
            <ToggleButton value="flow">Flow</ToggleButton>
            <ToggleButton value="bars">Bars</ToggleButton>
            <ToggleButton value="donut">Donut</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={tabBodySx}>
            <VendorFlowChart
              title={meta.title}
              rootLabel={meta.rootLabel}
              rows={selectedRows}
              view={widget.view}
              zoom={widget.zoom}
              gradientStart={widget.gradientStart || meta.color}
              gradientEnd={widget.gradientEnd || meta.color}
            />
          </Box>
        </Stack>
      </Box>
    );
  };

  const addNewChart = () => {
    const firstRow = rows[0];
    const firstCol = firstRow?.columns?.[0];
    if (firstRow?.id && firstCol?.id) {
      setAddChartTarget({ rowId: firstRow.id, colId: firstCol.id });
      setNewChart({
        dataset: "income",
        view: "flow",
        zoom: 1.25,
        gradientStart: "#10B981",
        gradientEnd: "#3B82F6",
      });
      setAddChartOpen(true);
      return;
    }
    setRows([
      {
        id: createId(),
        columns: [{ id: createId(), widgets: [createWidget()] }],
      },
    ]);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: createId(), columns: [createColumn()] }]);
  };

  const addColumn = (rowId: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, columns: [...row.columns, createColumn()] }
          : row
      )
    );
  };

  const openAddChartModal = (rowId: string, colId: string) => {
    setAddChartTarget({ rowId, colId });
    setNewChart({
      dataset: "income",
      view: "flow",
      zoom: 1.25,
      gradientStart: "#10B981",
      gradientEnd: "#3B82F6",
    });
    setAddChartOpen(true);
  };

  const addWidgetToColumn = (
    rowId: string,
    colId: string,
    widget: ChartWidget
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              columns: row.columns.map((col) =>
                col.id === colId
                  ? { ...col, widgets: [...col.widgets, widget] }
                  : col
              ),
            }
          : row
      )
    );
  };

  const handleConfirmAddChart = () => {
    if (!addChartTarget) return;
    const widget: ChartWidget = { id: createId(), ...newChart };
    addWidgetToColumn(addChartTarget.rowId, addChartTarget.colId, widget);
    setAddChartOpen(false);
    setAddChartTarget(null);
  };

  const deleteEmptyRow = (rowId: string) => {
    setRows((prev) => {
      const target = prev.find((row) => row.id === rowId);
      if (!target) return prev;
      const hasWidgets = target.columns.some((col) => col.widgets.length > 0);
      if (hasWidgets) return prev;
      return prev.filter((row) => row.id !== rowId);
    });
  };

  const deleteEmptyColumn = (rowId: string, columnId: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const target = row.columns.find((col) => col.id === columnId);
        if (!target) return row;
        if (target.widgets.length > 0) return row;
        return {
          ...row,
          columns: row.columns.filter((col) => col.id !== columnId),
        };
      })
    );
  };

  const updateWidget = (
    rowId: string,
    colId: string,
    widgetId: string,
    patch: Partial<ChartWidget>
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              columns: row.columns.map((col) =>
                col.id === colId
                  ? {
                      ...col,
                      widgets: col.widgets.map((widget) =>
                        widget.id === widgetId
                          ? { ...widget, ...patch }
                          : widget
                      ),
                    }
                  : col
              ),
            }
          : row
      )
    );
  };

  const removeWidget = (rowId: string, colId: string, widgetId: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              columns: row.columns.map((col) =>
                col.id === colId
                  ? {
                      ...col,
                      widgets: col.widgets.filter((w) => w.id !== widgetId),
                    }
                  : col
              ),
            }
          : row
      )
    );
  };

  const moveWidget = (toRowId: string, toColId: string) => {
    if (!dragging) return;
    if (dragging.fromRowId === toRowId && dragging.fromColId === toColId) {
      return;
    }

    setRows((prev) => {
      let movingWidget: ChartWidget | null = null;

      const removed = prev.map((row) => {
        if (row.id !== dragging.fromRowId) return row;
        return {
          ...row,
          columns: row.columns.map((col) => {
            if (col.id !== dragging.fromColId) return col;
            const found = col.widgets.find((w) => w.id === dragging.widgetId);
            if (found) movingWidget = found;
            return {
              ...col,
              widgets: col.widgets.filter((w) => w.id !== dragging.widgetId),
            };
          }),
        };
      });

      if (!movingWidget) return prev;

      return removed.map((row) =>
        row.id === toRowId
          ? {
              ...row,
              columns: row.columns.map((col) =>
                col.id === toColId
                  ? {
                      ...col,
                      widgets: [...col.widgets, movingWidget as ChartWidget],
                    }
                  : col
              ),
            }
          : row
      );
    });
  };

  if (loading) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ py: 6 }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading charts...</Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Charts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Build chart rows/columns and drag charts between them.
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75}>
          <Button
            variant="outlined"
            startIcon={<AddBoxIcon />}
            onClick={addRow}
          >
            Add Row
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addNewChart}
          >
            Add New Chart
          </Button>
        </Stack>
      </Stack>

      {rows.map((row) => (
        <Paper key={row.id} variant="outlined" sx={{ p: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Row
              </Typography>
              {!row.columns.some((col) => col.widgets.length > 0) && (
                <Tooltip title="Delete empty row">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteEmptyRow(row.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Button
              size="small"
              variant="text"
              startIcon={<AddBoxIcon />}
              onClick={() => addColumn(row.id)}
            >
              Add Column
            </Button>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.max(1, row.columns.length)}, minmax(280px, 1fr))`,
              gap: 1,
            }}
          >
            {row.columns.map((column) => (
              <Paper
                key={column.id}
                variant="outlined"
                onDragOver={(event) => {
                  event.preventDefault();
                  if (dragging) {
                    setDropHover({ rowId: row.id, colId: column.id });
                  }
                }}
                onDragLeave={(event) => {
                  const related = event.relatedTarget as Node | null;
                  if (related && event.currentTarget.contains(related)) return;
                  setDropHover((prev) => {
                    if (!prev) return prev;
                    if (prev.rowId !== row.id || prev.colId !== column.id)
                      return prev;
                    return null;
                  });
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  moveWidget(row.id, column.id);
                  setDragging(null);
                  setDropHover(null);
                }}
                sx={{
                  p: 0.75,
                  minHeight: 220,
                  bgcolor: "background.default",
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.25 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: "text.secondary" }}
                    >
                      Column
                    </Typography>
                    {column.widgets.length === 0 && row.columns.length > 1 && (
                      <Tooltip title="Delete empty column">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteEmptyColumn(row.id, column.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  {column.widgets.map((widget) =>
                    renderWidgetCard(widget, {
                      rowId: row.id,
                      colId: column.id,
                    })
                  )}

                  {column.widgets.length === 0 && (
                    <>
                      {dragging &&
                      dropHover?.rowId === row.id &&
                      dropHover?.colId === column.id ? (
                        (() => {
                          const widget = getWidgetById(dragging.widgetId);
                          if (!widget) return null;
                          return renderWidgetCard(widget, {
                            rowId: row.id,
                            colId: column.id,
                            ghost: true,
                          });
                        })()
                      ) : (
                        <Box
                          sx={{
                            minHeight: 140,
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                          onClick={() => openAddChartModal(row.id, column.id)}
                        >
                          Drop chart here
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              </Paper>
            ))}
          </Box>
        </Paper>
      ))}

      <Dialog
        open={addChartOpen}
        onClose={() => setAddChartOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Chart</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            <FormControl size="small">
              <InputLabel id="new-chart-dataset">Dataset</InputLabel>
              <Select
                labelId="new-chart-dataset"
                value={newChart.dataset}
                label="Dataset"
                onChange={(event) =>
                  setNewChart((prev) => ({
                    ...prev,
                    dataset: event.target.value as DatasetType,
                  }))
                }
              >
                <MenuItem value="income">Income</MenuItem>
                <MenuItem value="expenses">Expenses</MenuItem>
                <MenuItem value="debts">Debts</MenuItem>
              </Select>
            </FormControl>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={newChart.view}
              onChange={(event, value) =>
                value && setNewChart((prev) => ({ ...prev, view: value }))
              }
              sx={folderTabsSx}
            >
              <ToggleButton value="flow">Flow</ToggleButton>
              <ToggleButton value="bars">Bars</ToggleButton>
              <ToggleButton value="donut">Donut</ToggleButton>
            </ToggleButtonGroup>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Gradient
              </Typography>
              <Box
                component="input"
                type="color"
                value={newChart.gradientStart}
                onChange={(event) =>
                  setNewChart((prev) => ({
                    ...prev,
                    gradientStart: event.target.value,
                  }))
                }
                sx={{
                  width: 28,
                  height: 28,
                  p: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
              />
              <Box
                component="input"
                type="color"
                value={newChart.gradientEnd}
                onChange={(event) =>
                  setNewChart((prev) => ({
                    ...prev,
                    gradientEnd: event.target.value,
                  }))
                }
                sx={{
                  width: 28,
                  height: 28,
                  p: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
              />
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Zoom
              </Typography>
              <Tooltip title="Zoom out">
                <IconButton
                  size="small"
                  onClick={() =>
                    setNewChart((prev) => ({
                      ...prev,
                      zoom: clampZoom(prev.zoom - 0.1),
                    }))
                  }
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{ minWidth: 44, textAlign: "center" }}
              >
                {Math.round(newChart.zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom in">
                <IconButton
                  size="small"
                  onClick={() =>
                    setNewChart((prev) => ({
                      ...prev,
                      zoom: clampZoom(prev.zoom + 0.1),
                    }))
                  }
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset zoom">
                <IconButton
                  size="small"
                  onClick={() =>
                    setNewChart((prev) => ({
                      ...prev,
                      zoom: 1.25,
                    }))
                  }
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setAddChartOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmAddChart}
            disabled={!addChartTarget}
          >
            Add Chart
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Charts;
